/**
 * 地圖文字標籤 — 中國古典題簽風格
 *
 * 豎排文字 + LXGW 文楷 + 中式邊框，模擬古代地圖/名畫上的題簽
 * 編輯模式：雙擊編輯文字 · Shift+左鍵刪除 · 拖曳移動位置
 * 行走模式：半透明旗標
 */

import { useRef, useState, useCallback } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three/webgpu'
import { useGameStore, type MapLabel } from '../core/store/gameStore'
import { terrainBuffer } from '../core/terrainBuffer'
import { WATER_LEVEL } from '../core/terrainConstants'

// 題簽樣式常量
const LABEL_FONT = '"LXGW WenKai TC", "Noto Serif TC", serif'
const LABEL_COLOR_EDIT = '#3a2a1a'
const LABEL_COLOR_WALK = 'rgba(58,42,26,0.55)'
const BORDER_COLOR = 'rgba(90,70,45,0.65)'
const BG_COLOR = 'rgba(240,232,216,0.82)'
const BG_COLOR_WALK = 'rgba(240,232,216,0.4)'

/** 單個 3D 題簽標籤 */
function Label3D({ label, isEdit }: { label: MapLabel; isEdit: boolean }) {
  const groupRef = useRef<THREE.Group>(null)
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const composingRef = useRef(false)
  const updateLabel = useGameStore(s => s.updateLabel)
  const removeLabel = useGameStore(s => s.removeLabel)
  const updateLabelPosition = useGameStore(s => s.updateLabelPosition)

  // 拖曳狀態
  const draggingRef = useRef(false)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null)
  const { camera, gl } = useThree()
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), -WATER_LEVEL))

  useFrame(() => {
    if (!groupRef.current || !terrainBuffer.ready) return
    const y = terrainBuffer.getHeight(label.position[0], label.position[2])
    groupRef.current.position.set(label.position[0], y + 12, label.position[2])
  })

  // 確認編輯 — 從 ref 讀值（非受控模式，避免 IME 輸入問題）
  const confirmEdit = useCallback(() => {
    if (composingRef.current) return
    const val = inputRef.current?.value.trim() ?? ''
    if (val) {
      updateLabel(label.id, val)
    } else {
      removeLabel(label.id)
    }
    setEditing(false)
  }, [label.id, updateLabel, removeLabel])

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (!isEdit) return
    e.stopPropagation()
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [isEdit])

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!isEdit || !e.shiftKey) return
    e.stopPropagation()
    removeLabel(label.id)
  }, [isEdit, label.id, removeLabel])

  // 拖曳：pointer down/move/up
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isEdit || editing || e.shiftKey) return
    e.stopPropagation()
    dragStartRef.current = { x: e.clientX, y: e.clientY }
  }, [isEdit, editing])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isEdit || !dragStartRef.current) return
    // 開始拖曳的閾值（5px）
    if (!draggingRef.current) {
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      if (Math.sqrt(dx * dx + dy * dy) < 5) return
      draggingRef.current = true
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    }

    // 光線投射到地面取新位置
    const rect = gl.domElement.getBoundingClientRect()
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera)
    const hit = new THREE.Vector3()
    if (raycaster.ray.intersectPlane(groundPlane.current, hit)) {
      if (terrainBuffer.ready) {
        hit.y = Math.max(terrainBuffer.getHeight(hit.x, hit.z), WATER_LEVEL)
      }
      updateLabelPosition(label.id, [hit.x, hit.y, hit.z])
    }
  }, [isEdit, camera, gl.domElement, label.id, updateLabelPosition])

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (draggingRef.current) {
      e.stopPropagation()
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    }
    draggingRef.current = false
    dragStartRef.current = null
  }, [])

  // 題簽外框樣式 — 模擬古地圖上的矩形題簽
  const tagStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 6px',
    background: isEdit ? BG_COLOR : BG_COLOR_WALK,
    // 中式雙線邊框
    border: `1.5px solid ${BORDER_COLOR}`,
    outline: `1px solid ${isEdit ? 'rgba(90,70,45,0.25)' : 'rgba(90,70,45,0.1)'}`,
    outlineOffset: '3px',
    // 豎排文字
    writingMode: 'vertical-rl',
    textOrientation: 'upright',
    fontFamily: LABEL_FONT,
    fontWeight: 700,
    fontSize: isEdit ? '15px' : '12px',
    color: isEdit ? LABEL_COLOR_EDIT : LABEL_COLOR_WALK,
    letterSpacing: '0.2em',
    lineHeight: 1.6,
    cursor: isEdit ? 'grab' : 'default',
    userSelect: 'none',
    transition: 'opacity 0.3s, transform 0.3s',
    // 微妙的紙質感投影
    boxShadow: isEdit
      ? '2px 2px 8px rgba(0,0,0,0.15), inset 0 0 12px rgba(180,160,130,0.1)'
      : '1px 1px 4px rgba(0,0,0,0.08)',
  }

  // 編輯中的輸入框（橫排，方便打字）
  const inputStyle: React.CSSProperties = {
    background: 'rgba(240,232,216,0.95)',
    color: LABEL_COLOR_EDIT,
    border: `1.5px solid ${BORDER_COLOR}`,
    padding: '6px 10px',
    fontSize: '15px',
    fontFamily: LABEL_FONT,
    fontWeight: 700,
    textAlign: 'center',
    outline: 'none',
    minWidth: '60px',
    letterSpacing: '0.1em',
  }

  return (
    <group ref={groupRef}>
      <Html
        center
        distanceFactor={180}
        occlude={false}
        style={{ pointerEvents: isEdit ? 'auto' : 'none' }}
        zIndexRange={[50, 0]}
      >
        {editing ? (
          <input
            ref={inputRef}
            defaultValue={label.text}
            onCompositionStart={() => { composingRef.current = true }}
            onCompositionEnd={() => { composingRef.current = false }}
            onBlur={() => setTimeout(confirmEdit, 50)}
            onKeyDown={e => {
              e.stopPropagation()
              if (composingRef.current) return
              if (e.key === 'Enter') { e.preventDefault(); confirmEdit() }
              if (e.key === 'Escape') setEditing(false)
            }}
            style={inputStyle}
          />
        ) : (
          <div
            onDoubleClick={handleDoubleClick}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            style={tagStyle}
          >
            {label.text}
          </div>
        )}
      </Html>
    </group>
  )
}

export default function MapLabels() {
  const labels = useGameStore(s => s.mapLabels)
  const editorMode = useGameStore(s => s.editorMode)
  const isEdit = editorMode === 'edit'

  return (
    <>
      {labels.map(label => (
        <Label3D key={label.id} label={label} isEdit={isEdit} />
      ))}
    </>
  )
}
